export const CHAT_SECTIONS = [
  { id: 'agents-teams', label: 'Agentes e times' },
  { id: 'agents', label: 'Agentes' },
  { id: 'teams', label: 'Times / filas' },
  { id: 'inbox', label: 'Configuração Inbox' },
];

export const DAY_LABELS = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];

export const ROLE_LABELS = {
  agent: 'Agente',
  administrator: 'Administrador',
};

export function chatStatusHint(status) {
  if (status == null || status === 'pending') {
    return 'Seu chat ainda está sendo configurado pela Verve.';
  }
  if (status === 'suspended') {
    return 'Chat temporariamente indisponível — contate o suporte da Verve.';
  }
  if (status === 'error') {
    return 'Houve um problema técnico ao configurar seu chat. Contate o suporte.';
  }
  return '';
}

export function canUseChatAdmin(chatwootStatus, chatEnabled) {
  return chatwootStatus === 'active' && Boolean(chatEnabled);
}

export function normalizeAgentsList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.payload)) return data.payload;
  return [];
}

export function teamMemberIds(team) {
  if (Array.isArray(team?.memberIds) && team.memberIds.length) {
    return team.memberIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }
  const raw =
    team?.members ||
    team?.team_members ||
    team?.agents ||
    team?.agent_ids ||
    team?.user_ids ||
    [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((m) => {
      if (m == null) return null;
      if (typeof m === 'number' || typeof m === 'string') return Number(m);
      return Number(m.id ?? m.user_id ?? m.userId ?? m.agent_id);
    })
    .filter((id) => Number.isFinite(id) && id > 0);
}

export function defaultWorkingHours() {
  return Array.from({ length: 7 }, (_, day) => ({
    day_of_week: day,
    closed_all_day: day === 0 || day === 6,
    open_hour: 9,
    open_minutes: 0,
    close_hour: 18,
    close_minutes: 0,
    open_all_day: false,
  }));
}

function coerceHour(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Normaliza 1 dia — evita nulls do Chatwoot em dias abertos (causam 500 na API). */
export function normalizeWorkingHourDay(raw, fallbackDay = 0) {
  const base = defaultWorkingHours()[Math.min(6, Math.max(0, Number(fallbackDay) || 0))];
  const day = Number(raw?.day_of_week);
  const dayOfWeek = Number.isFinite(day) && day >= 0 && day <= 6 ? day : base.day_of_week;
  const closed = Boolean(raw?.closed_all_day);
  const openAllDay = Boolean(raw?.open_all_day) && !closed;

  if (closed) {
    return {
      day_of_week: dayOfWeek,
      closed_all_day: true,
      open_all_day: false,
      open_hour: null,
      open_minutes: null,
      close_hour: null,
      close_minutes: null,
    };
  }

  if (openAllDay) {
    return {
      day_of_week: dayOfWeek,
      closed_all_day: false,
      open_all_day: true,
      open_hour: 0,
      open_minutes: 0,
      close_hour: 23,
      close_minutes: 59,
    };
  }

  let openHour = coerceHour(raw?.open_hour, 9);
  let openMinutes = coerceHour(raw?.open_minutes, 0);
  let closeHour = coerceHour(raw?.close_hour, 18);
  let closeMinutes = coerceHour(raw?.close_minutes, 0);

  openHour = Math.min(23, Math.max(0, openHour));
  closeHour = Math.min(23, Math.max(0, closeHour));
  openMinutes = Math.min(59, Math.max(0, openMinutes));
  closeMinutes = Math.min(59, Math.max(0, closeMinutes));

  const openTotal = openHour * 60 + openMinutes;
  const closeTotal = closeHour * 60 + closeMinutes;
  if (closeTotal <= openTotal) {
    closeHour = Math.min(23, openHour + 1);
    closeMinutes = openMinutes;
  }

  return {
    day_of_week: dayOfWeek,
    closed_all_day: false,
    open_all_day: false,
    open_hour: openHour,
    open_minutes: openMinutes,
    close_hour: closeHour,
    close_minutes: closeMinutes,
  };
}

export function mergeWorkingHours(incoming) {
  const base = defaultWorkingHours();
  if (!Array.isArray(incoming)) return base;
  const byDay = new Map(
    incoming
      .map((w) => [Number(w.day_of_week), w])
      .filter(([d]) => Number.isFinite(d) && d >= 0 && d <= 6)
  );
  return base.map((d) => normalizeWorkingHourDay(byDay.get(d.day_of_week) || d, d.day_of_week));
}

/** Payload limpo para PATCH /inbox (só attrs aceitos pelo Chatwoot). */
export function serializeWorkingHoursForSave(hours) {
  return mergeWorkingHours(hours).map((w) => normalizeWorkingHourDay(w, w.day_of_week));
}

/** Fuso padrão do inbox (Brasília) — backend também força no PATCH. */
export const CHAT_INBOX_TIMEZONE = 'America/Sao_Paulo';

export function buildInboxSettingsPayload({
  greetingEnabled,
  greetingMessage,
  workingHoursEnabled,
  outOfOfficeMessage,
  workingHours,
  blockOutsideHours,
  groupConversationsByClient,
}) {
  return {
    greeting_enabled: greetingEnabled,
    greeting_message: greetingMessage,
    working_hours_enabled: workingHoursEnabled,
    out_of_office_message: outOfOfficeMessage,
    working_hours: serializeWorkingHoursForSave(workingHours),
    timezone: CHAT_INBOX_TIMEZONE,
    block_outside_hours: Boolean(blockOutsideHours),
    group_conversations_by_client: Boolean(groupConversationsByClient),
  };
}
